// Nylon-string guitar for the bossa style (docs/AUDIO_DESIGN.md §12). There is no AudioWorklet and a DelayNode in a
// feedback loop can't go under 128 samples, so every pluck is an extended Karplus–Strong string rendered in JS into an
// AudioBuffer (cached per page) and played by one AudioBufferSource + one gain per note, through a shared body bus.
import { rng, clamp } from '../../engine/core.js';
import { mtof } from '../dsp.js';
import { gain, filter, panner } from '../instruments.js';

/* ---------------- plucks: extended Karplus–Strong at half the context rate ---------------- */
// y[n] = e[n] for the first lap (n < L), then T(D(H(y[n − L])))   e = the pluck, H = loss + damping one-pole,
// D = dispersion allpass, T = tuning allpass; the output buffer is the delay line. Rendered at half the context rate
// (the bus LP sits at 2.6–5.6 kHz, nothing above 11 kHz is lost; the source node resamples), which halves the JS time
// and the memory: ≈ 0.35 ms and 100–230 KB per pluck, ~60 plucks at most per page.
const CACHE = new Map();                              // `${rate}:${midi}:${role}` → AudioBuffer, built once, lazily
export const PLUCK_STATS = { built: 0, ms: 0, maxMs: 0 };
const FB = 1800;                                      // the loop filter meets the T60 at f0 and at 1.8 kHz
//   pick position β · excitation LP (Hz) · noise share · attack ramp (s) · T60 at 1.8 kHz (s)
const ROLES = {
  thumb: { beta: 0.24, lp: 1100, noise: 0.2, atk: 0.005, tb: 0.2 },      // flesh of the thumb: round, dark
  finger: { beta: 0.13, lp: 2600, noise: 0.35, atk: 0.0015, tb: 0.36 },  // fingertip + a little nail
};
const TWO_PI = Math.PI * 2;
const apDelay = (c, w) => 1 - 2 * Math.atan2(c * Math.sin(w), 1 + c * Math.cos(w)) / w;   // 1st-order allpass phase delay

function render(rate, midi, role) {
  const R = ROLES[role], f0 = mtof(midi), P = rate / f0, w0 = TWO_PI * f0 / rate, wb = TWO_PI * FB / rate;
  // T60 of the fundamental falls with pitch (E2 5.3 s · G3 3.6 s · E5 2.1 s); the buffer is 0.6 of it, 1.3–2.4 s
  const T0 = clamp(3.6 * Math.pow(196 / f0, 0.45), 1.3, 5.5), N = Math.round(clamp(0.6 * T0, 1.3, 2.4) * rate);
  // loss + damping: H(z) = g(1 − a)/(1 − a z⁻¹) with |H| = 10^(−3/(f0·T60)) at f0 and at 1.8 kHz (bisection on a)
  const A0 = Math.pow(10, -3 / (f0 * T0)), want = Math.pow(10, -3 / (f0 * R.tb)) / A0;
  const mag = (a, w) => (1 - a) / Math.sqrt(1 - 2 * a * Math.cos(w) + a * a);
  let lo = 0, hi = 0.97;
  for (let k = 0; k < 40; k++) { const m = (lo + hi) / 2; if (mag(m, wb) / mag(m, w0) > want) lo = m; else hi = m; }
  const a = (lo + hi) / 2, g = A0 / mag(a, w0), b0 = g * (1 - a);
  // dispersion (a touch of string stiffness, more on the wound strings) and tuning: the loop's phase delay at f0 = P
  const cd = midi < 52 ? -0.12 : -0.06, rest = P - Math.atan2(a * Math.sin(w0), 1 - a * Math.cos(w0)) / w0 - apDelay(cd, w0);
  const L = Math.max(1, Math.floor(rest - 0.5)), d = rest - L;
  let cl = -0.9, ch = 0.9;
  for (let k = 0; k < 40; k++) { const m = (cl + ch) / 2; if (apDelay(m, w0) > d) cl = m; else ch = m; }
  const C = (cl + ch) / 2;
  // excitation = the string's first lap (L samples): a triangle pluck peaking at β (its comb nulls are the pick
  // position) + combed noise, rounded by the flesh (a periodic one-pole LP run twice: 12 dB/oct, no corner to click),
  // mean removed (no DC in the loop). The loop filters are primed with that lap, as if it had been ringing already, so
  // the second lap follows the first without a step.
  const pk = Math.max(1, Math.round(R.beta * L)), e = new Float64Array(L), nz = new Float64Array(L);
  const r = rng((midi * 7919 + (role === 'thumb' ? 17 : 29)) >>> 0), al = 1 - Math.exp(-TWO_PI * R.lp / rate);
  const lp2 = x => { let z = 0, z2 = 0; for (let lap = 0; lap < 2; lap++) for (let i = 0; i < L; i++) { z += al * (x[i] - z); z2 += al * (z - z2); if (lap) x[i] = z2; } };
  for (let i = 0; i < L; i++) nz[i] = r() * 2 - 1;
  const nc = Float64Array.from(nz, (v, i) => v - nz[(i - pk + L) % L]);
  let ns = 0, mean = 0;
  for (let i = 0; i < L; i++) ns += nc[i] * nc[i];
  const nk = R.noise * 0.3 / (Math.sqrt(ns / L) || 1);
  for (let i = 0; i < L; i++) e[i] = (1 - R.noise) * (i <= pk ? i / pk : (L - i) / (L - pk)) + nk * nc[i];
  lp2(e);
  for (let i = 0; i < L; i++) mean += e[i] / L;
  for (let i = 0; i < L; i++) e[i] -= mean;
  let h = 0, dx = 0, dy = 0, tx = 0, ty = 0;
  const lap = x => {
    h = b0 * x + a * h;
    const dv = cd * h + dx - cd * dy; dx = h; dy = dv;
    const tv = C * dv + tx - C * ty; tx = dv; ty = tv;
    return tv;
  };
  for (let i = 0; i < L; i++) lap(e[i]);
  const y = new Float32Array(N);
  for (let n = 0; n < N; n++) y[n] = n < L ? e[n] : lap(y[n - L]);
  // what the top hears is the force at the bridge, the string's slope: y[n] − y[n−1] (+6 dB/oct: centroid ≈ 1.6·f0 for
  // the thumb, 2–2.5·f0 for the fingers in the first 0.5 s), then a DC blocker (~20 Hz), the attack ramp (thumb 5 ms,
  // finger 1.5 ms), a 0.25 s end fade, RMS of 0–0.3 s → 0.2
  const hp = 1 - TWO_PI * 20 / rate, na = Math.max(2, Math.round(R.atk * rate)), nf = Math.round(0.25 * rate);
  let px = 0, py = 0, ss = 0, yl = 0;
  const n3 = Math.min(N, Math.round(0.3 * rate));
  for (let n = 0; n < N; n++) {
    const s = y[n] - yl; yl = y[n];
    py = s - px + hp * py; px = s;
    let v = py;
    if (n < na) v *= 0.5 - 0.5 * Math.cos(Math.PI * n / na);
    if (n >= N - nf) v *= 0.5 + 0.5 * Math.cos(Math.PI * (n - N + nf) / nf);
    y[n] = v;
    if (n < n3) ss += v * v;
  }
  const k = 0.2 / (Math.sqrt(ss / n3) || 1);
  for (let n = 0; n < N; n++) y[n] *= k;
  return y;
}

/** The cached pluck of one note ('thumb' | 'finger'), rendered on first use. */
export function pluck(ac, midi, role) {
  const rate = ac.sampleRate >= 44100 ? Math.round(ac.sampleRate / 2) : ac.sampleRate, key = `${rate}:${midi}:${role}`;
  let b = CACHE.get(key);
  if (!b) {
    const t = performance.now(), y = render(rate, midi, role);
    b = ac.createBuffer(1, y.length, rate);
    b.getChannelData(0).set(y);
    CACHE.set(key, b);
    const ms = performance.now() - t;
    PLUCK_STATS.built++; PLUCK_STATS.ms += ms; PLUCK_STATS.maxMs = Math.max(PLUCK_STATS.maxMs, ms);
  }
  return b;
}

/* ---------------- the instrument ---------------- */
// Levels (tuning knobs): the guitar against the EP it replaces, the thumb against the fingers.
export const NYLON = { level: 6, thumb: 1.1, send: 0.28 };
const PANS = [-0.16, -0.05, 0.06, 0.17];              // thumb (bass strings) · lower · middle · top finger

/**
 * createNylon(ac, out, send, p, opts) → { chord, note, warm, stats, dispose }   (the style's `ep`, or a lead)
 * p: { lp } (the bus low-pass). opts: { registry, tag, cap, r, t0, log, stepDur, pan (offset), spread, level, sendLevel }.
 * Bus: 4 panners → body 105 Hz (+3 dB, Q 1.8) → top plate 215 Hz (+2 dB, Q 1.6) → presence 2.6 kHz (+2.5 dB, Q .9)
 * → allpasses 500 / 1300 / 3000 Hz (Q .7: the body smears the bridge pulse, −3 dB of crest, same spectrum) → LP p.lp
 * (Q .5) → out, reverb send .28. A note on a string that is still ringing damps it (τ 12 ms): the thumb has its own
 * string, the fingers take strings from the top down, so the comping never has more than 4–5 voices sounding.
 */
export function createNylon(ac, out, send, p, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null,
  stepDur = 0.2, pan = 0, spread = 1, level = 1, sendLevel = NYLON.send }) {
  const pans = PANS.map(x => panner(ac, clamp(pan + x * spread, -1, 1))), busIn = gain(ac, 1);
  const body = filter(ac, 'peaking', 105, 1.8, 3), plate = filter(ac, 'peaking', 215, 1.6, 2), air = filter(ac, 'peaking', 2600, 0.9, 2.5);
  const aps = [500, 1300, 3000].map(f => filter(ac, 'allpass', f, 0.7));
  const lp = filter(ac, 'lowpass', p.lp, 0.5), outG = gain(ac, level * NYLON.level), sendG = gain(ac, sendLevel);
  const chain = [busIn, body, plate, air, ...aps, lp, outG];
  for (const q of pans) q.connect(busIn);
  chain.forEach((n, k) => k && chain[k - 1].connect(n));
  outG.connect(out); outG.connect(sendG); sendG.connect(send);
  const bus = registry.add({ src: [], all: [...pans, ...chain, sendG], start: t0, end: Infinity, tag: `${tag}:p` });
  let voices = [], maxActive = 0, steals = 0, notes = 0, pending = [];

  // damp a ringing string (a re-pluck scheduled later than t is cancelled outright by registry.stop)
  const damp = (x, t) => { registry.stop(x.v, t, 0.012); x.end = t; if (x.rec) x.rec[1] = Math.max(x.rec[0], Math.min(x.rec[1], t)); };
  /** one pluck: role 'thumb' | 'finger', str = the string it sounds on, pi = panner index */
  function note(t, midi, vel, dur, relTau = 0.08, role = 'finger', str = `m${midi}`, pi = 2) {
    const buf = pluck(ac, midi, role);
    voices = voices.filter(x => x.end > t);
    for (const x of voices) if (x.str === str || x.midi === midi) damp(x, t);
    voices = voices.filter(x => x.end > t);
    if (voices.length >= cap) {                     // steal the oldest (10 ms release)
      const old = voices.shift();
      registry.stop(old.v, t, 0.01); old.end = t; if (old.rec) old.rec[1] = t;
      steals++;
    }
    const rate = Math.pow(2, (r() * 6 - 3) / 1200);              // ±3 ¢ per pluck
    const src = ac.createBufferSource(), g = gain(ac, 0);
    src.buffer = buf; src.playbackRate.value = rate;
    src.connect(g); g.connect(pans[pi]);
    const A = 0.16 * Math.pow(vel, 1.3) * (role === 'thumb' ? NYLON.thumb : 1), off = t + Math.max(0.05, dur);
    g.gain.setValueAtTime(A, t);
    g.gain.setTargetAtTime(0, off, relTau);
    const bufEnd = t + buf.duration / rate, end = Math.min(bufEnd, off + relTau * 7);
    src.start(t); src.stop(end);
    const v = registry.add({ src: [src], all: [src, g], start: t, end, tag, out: g });
    const x = { v, str, midi, start: t, end: Math.min(bufEnd, off + relTau * 4), rec: null };
    if (log) { x.rec = [t, x.end]; log.push(x.rec); }
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  /** strings and panners of a chord call: a single low note or the lowest of 4+ is the thumb, the rest fingers */
  function layout(midis) {
    const n = midis.length;
    return midis.map((m, k) => {
      const thumb = k === 0 && (n === 1 || n >= 4) && m < 57;
      if (thumb) return { m, role: 'thumb', str: 'b', pi: 0 };
      if (n === 1) return { m, role: 'finger', str: `m${m}`, pi: m < 60 ? 1 : m < 67 ? 2 : 3 };
      const f = n - 1 - k;                           // strings from the top: f0 = the top voice
      return { m, role: 'finger', str: `f${f}`, pi: clamp(3 - f, 1, 3) };
    });
  }
  /** held chords (intro, break, outro: rel ≥ .1) as a guitarist plays them: a p-i-m-a roll on beat 1 and thumb +
   *  pinch on beat 3, again every 2 beats while it lasts; the final chord (rel ≥ .5) is one slow roll left to ring. The
   *  strikes after the first count back from the chord's end on the grid (t + dur + 30 ms: the engine's hits end 30 ms
   *  early), so a chord struck again part-way through (the radio tuning in) plays on exactly the strikes it had. */
  function held(t, midis, vel, dur, relTau) {
    const L = layout(midis), half = 8 * stepDur, final = relTau >= 0.5, end = t + dur + 0.03;
    const gap = final ? 0.14 : clamp(0.55 * stepDur, 0.07, 0.12);
    const strike = (tj, left, pinch, vj) => L.forEach((x, k) => {
      const dt = pinch ? (k ? 0.012 + 0.004 * k : 0) : k * gap * (1 + (r() - 0.5) * 0.2);
      note(tj + dt, x.m, vj * (x.role === 'thumb' ? 0.95 : 0.85 + 0.05 * k), left - dt, relTau, x.role, x.str, x.pi);
    });
    strike(t, dur, false, vel);
    let halves = 0;                                  // up to 3 more, a half bar apart back from the end, after now
    while (!final && halves < 3 && end - (halves + 1) * half > t + 0.05) halves++;
    for (let j = halves; j >= 1; j--) {             // j halves before the end, in time order (a pluck damps its string)
      const tj = end - j * half, pinch = j % 2 === 1;
      strike(tj, end - 0.03 - tj, pinch, vel * (pinch ? 0.72 : 1) * 0.9);
    }
  }
  return {
    note,
    /** strummed bottom to top over `strum` s (a finger pinch is nearly together); the thumb rings ≥ 6 steps and the
     *  top voice gets +6 % velocity. Each call also renders one pluck still missing from warm()'s list. */
    chord(t, midis, vel, dur, strum = 0.012, relTau = 0.08) {
      if (relTau >= 0.095 && midis.length >= 3 && dur >= 7.5 * stepDur) held(t, midis, vel, dur, relTau);
      else {
        const L = layout(midis), n = L.length;
        L.forEach((x, k) => {
          const dt = n > 1 ? (k * strum) / (n - 1) : 0, d = x.role === 'thumb' ? Math.max(dur, 6 * stepDur) : dur;
          const v = vel * (k === n - 1 && n > 1 ? 1.06 : 1) * (1 + (r() * 2 - 1) * 0.05);
          note(t + dt, x.m, Math.min(1, v), d - dt, relTau, x.role, x.str, x.pi);
        });
      }
      if (pending.length) { const [m, role] = pending.shift(); pluck(ac, m, role); }
    },
    /** render the plucks of these voicings (the track's chords): now within `ms` of JS, the rest one per chord call,
     *  so no scheduler tick renders more than the few a chord needs for the first time */
    warm(chords, ms = 3) {
      const t = performance.now(), seen = new Set();
      for (const v of chords) for (const x of layout(v)) {
        const k = `${x.m}:${x.role}`;
        if (seen.has(k)) continue;
        seen.add(k);
        if (performance.now() - t < ms) pluck(ac, x.m, x.role); else pending.push([x.m, x.role]);
      }
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.end = t; },
  };
}
